<template>
  <div>
    <SkinnyBanner page="product" />

    <RevContainer
      v-if="productResponse"
      class="bg-surface-default-low pb-18 md:pb-48"
    >
      <Breadcrumb
        v-if="breadcrumbResponse"
        id="overview"
        :breads="[
          ...breadcrumbResponse.breadcrumb,
          { label: productResponse.titles.raw, url: route.path },
        ]"
        class="bg-surface-default-low"
        trackingZone="product"
      />
      <OutOfStock
        v-if="isOutOfStock"
        :alternative="outOfStockAlternative"
        :breadcrumb="breadcrumbResponse?.breadcrumb ?? []"
        :is-alternative-loading="outOfStockAlternativeLoading"
        :product="productResponse"
        :ratings="ratesResponse"
      />

      <div
        v-if="!isOutOfStock"
        class="flex flex-col flex-wrap md:flex-row md:flex-nowrap"
      >
        <div class="relative w-full max-w-full grow-0 md:w-1/3 lg:w-1/2">
          <div class="top-[7rem] md:sticky md:right-1/2 md:mr-56">
            <Gallery
              allow-reassurance-images
              :images="productResponse.images"
              :product="productResponse"
              should-preload-first-img
              :tracking-product-model="productResponse.model"
              tracking-zone="pp_content_carousel"
            />

            <CheckpointsCarousel
              v-if="productResponse"
              :category-id="productResponse.tracking.categoryId"
              class="mt-32 hidden md:block"
            />
          </div>
        </div>

        <TheMainContent
          class="w-full max-w-full grow-0 md:w-2/3 md:basis-2/3 lg:w-1/2 lg:basis-1/2"
        >
          <div class="flex flex-col items-stretch md:flex-col-reverse">
            <div>
              <div
                class="mt-32 flex h-32 items-center"
                :class="{
                  'justify-between': productResponse.showVerifiedRefurbished,
                  'justify-end': !productResponse.showVerifiedRefurbished,
                }"
              >
                <VerifiedRefurbishedTag
                  v-if="productResponse.showVerifiedRefurbished"
                />
                <FavoritesToggle
                  class="bg-static-default-low rounded-full"
                  :grade-id="selectedOffer?.grade.value"
                  is-rounded
                  :offer-type
                  :price="selectedOffer?.price"
                  :product-uuid="productResponse.productId"
                  :tracking-info="favoritesTrackingInfo"
                  zone="details"
                />
              </div>
              <TitleDesktop
                v-if="price"
                :aria-hidden="isMobile"
                class="hidden md:mt-8 md:block"
                :popularity-stats-sales="popularityStatsSalesResponse"
                :price
                :product="productResponse"
                :rating="ratesResponse"
                :selected-offer
                :tracking="productTracking"
              />
              <TitleMobile
                v-if="price"
                ref="titleMobileButtonVisibilityBeacon"
                :aria-hidden="!isMobile"
                class="mt-8 md:hidden"
                hide-favorites-button
                :popularity-stats-sales="popularityStatsSalesResponse"
                :price
                :product="productResponse"
                :rating="ratesResponse"
              />
              <TitleMobileAtc
                v-if="price"
                ref="addToCartTitleMobileButtonVisibilityBeacon"
                class="md:hidden"
                :price
                :product="productResponse"
                :rating="ratesResponse"
                :selected-offer
                :tracking="productTracking"
              />
            </div>
          </div>

          <BlackFridaySpecial
            v-if="isBlackFridaySpecialActive"
            class="mb-20 mt-6"
          />

          <PointsSection v-if="price" class="mb-20" :price />

          <RevDivider v-if="price" class="my-16" />
          <AdvertisedPayment class="hidden md:block" :selected-offer />
          <ReassuranceItems
            v-if="productResponse && selectedOffer"
            :category-id="productResponse.tracking.categoryId"
            :shipping="selectedOffer.shipping"
            :warranty-delay="selectedOffer.defaultWarrantyDelay"
          />

          <RevDivider v-if="price" class="my-16 hidden md:block" />
          <PaymentMethods
            v-if="selectedOffer?.paymentMethods"
            class="hidden md:block"
            :methods="selectedOffer.paymentMethods"
          />
          <RevDivider class="my-16 hidden md:block" />
          <CustomizationFunnelWrapper
            v-if="
              productResponse &&
              selectedOffer &&
              !isOutOfStock &&
              price &&
              pickersResponse
            "
            :breadcrumb="breadcrumbResponse"
            :is-services-pickers-response-pending="false"
            :pickers-response
            :pickers-services="null"
            :price
            :price-without-subsidies
            :price-without-trade-in
            :product="productResponse"
            :product-tracking
            :review-count="ratesResponse?.count || 0"
            :selected-offer
            :technical-specifications="technicalSpecificationsResponse"
            @hide="handleHideTopBar"
            @show="handleShowTopBar"
          />

          <MobileAtc
            v-if="price"
            ref="addToCartMobileButtonVisibilityBeacon"
            class="mb-20 mt-32 md:hidden"
            :funnel-data
            :price
            :product="productResponse"
            :rating="ratesResponse"
            :selected-offer
            :title="productResponse.titles.raw"
            :tracking="productTracking"
          />
          <AdvertisedPayment class="md:hidden" :selected-offer />
          <ReassuranceItems
            v-if="productResponse && selectedOffer"
            :category-id="productResponse.tracking.categoryId"
            class="md:hidden"
            :shipping="selectedOffer.shipping"
            :warranty-delay="selectedOffer.defaultWarrantyDelay"
          />

          <RevDivider v-if="price" class="my-16 md:hidden" />
          <PaymentMethods
            v-if="selectedOffer?.paymentMethods"
            class="md:hidden"
            :methods="selectedOffer.paymentMethods"
          />
          <RevDivider class="my-16 md:hidden" />
          <Accessories
            class="mb-32"
            :comes-with="productResponse.comesWith"
            :partner-promo-codes="
              productResponse.includedServiceOffers.partnerPromoCodes
            "
          />

          <Seller
            v-if="productResponse && selectedOffer && selectedOffer.merchant"
            :company="selectedOffer.merchant.company"
            :merchant-id="selectedOffer.merchant.merchantId"
            :merchant-url="selectedOffer.merchant.merchantUrl"
          />

          <AdditionalInfo
            v-if="
              technicalSpecificationsResponse &&
              selectedOffer &&
              selectedOffer.offerLegacyId
            "
            :instructions="productResponse.instructions"
            :listing-id="selectedOffer.offerLegacyId"
            :product-title="productResponse.titles.default"
            :technical-specifications="technicalSpecificationsResponse"
          />
        </TheMainContent>
      </div>
    </RevContainer>

    <CheckpointsCarousel
      v-if="productResponse"
      :category-id="productResponse.tracking.categoryId"
      class="pb-48 md:hidden"
    />

    <RevContainer class="bg-surface-default-low">
      <TabNavigation
        id="JapanProductPageTabNavigationInPage"
        :review-count="ratesResponse?.count || 0"
      />
    </RevContainer>

    <RevContainer>
      <ProductConditionBlock
        v-if="pickersResponse && productResponse"
        id="product-feature"
        :brand="productResponse?.brand"
        :pickersResponse
      />
    </RevContainer>

    <RevContainer class="my-32">
      <AboutBatteryBlock
        v-if="productResponse"
        :category-id="productResponse.tracking.categoryId"
      />
    </RevContainer>

    <RevContainer v-if="isHygieneBlockActive" class="my-24 py-32">
      <AboutHygieneBlock />
    </RevContainer>

    <RevContainer>
      <SeoContent
        v-if="productResponse && technicalSpecificationsResponse"
        class="mt-32"
        data-id="seo-technical-specs"
        :description="productResponse.seo.description ?? ''"
        :model="productResponse.model"
        :technical-specifications="technicalSpecificationsResponse"
        :title="productResponse.titles.raw"
      />
    </RevContainer>

    <BlockSection
      v-if="cmsSections"
      class="mb-32 mt-32"
      :section="cmsSections.section1"
    />

    <RevContainer
      v-if="productResponse && ratesResponse?.count"
      class="mb-56 mt-32 md:mt-72"
    >
      <ReviewsBlock
        id="reviews"
        :ai-summary="reviewsAISummary"
        :ai-summary-pending="reviewsAiSummaryPending"
        :is-jp="true"
        :merchant-id="selectedOffer?.merchant?.merchantId"
        :merchant-url="selectedOffer?.merchant?.merchantUrl"
        :next-reviews-cursor="reviewsResponse?.nextCursor"
        :pending-summary="reviewsSummaryPending"
        :previous-reviews-cursor="reviewsResponse?.previousCursor"
        :product-id="productResponse.productId"
        :product-model="productResponse.model"
        :product-title="productResponse.titles.default"
        :rating="ratesResponse"
        :rating-summary="reviewsSummaryResponse"
        :reviews="reviews.list"
        :reviews-pending="reviews.isPending"
        :tracking-info="productTracking.product.value"
      />
    </RevContainer>

    <BlockSection
      v-if="cmsSections"
      class="mb-32"
      :section="cmsSections.section2"
    />

    <BlockSection v-if="cmsSections" :section="cmsSections.section3" />

    <RevContainer
      v-if="productResponse && !errorReco"
      class="mt-56"
      data-id="reco-section"
    >
      <Recommendations
        :product="productResponse"
        :selected-offer
        with-ranking
        @bundle-add-to-cart="onBundleAddToCart"
        @recommendations-error="onRecommendationsError"
        @recommendations-loaded="onRecommendationsLoaded"
      />
    </RevContainer>

    <RevContainer v-if="productResponse">
      <SeoLinks
        v-if="seoLinksList"
        class="my-32 md:my-72"
        :links="seoLinksList"
      />
    </RevContainer>

    <BlockSection
      v-if="cmsSections"
      class="mb-56 mt-32"
      :section="cmsSections.section4"
    />

    <div class="bg-surface-default-low py-48">
      <RevContainer>
        <div class="flex flex-col gap-32 md:gap-64">
          <ReassuranceWarranty id="warranty" />
          <ReassuranceInsuranceReturn />
          <ReassuranceCustomerSupport />
          <ReassuranceMarketplace />
          <ReassuranceAboutUs id="about" />
        </div>
      </RevContainer>
    </div>

    <BlockSection v-if="cmsSections" :section="cmsSections.section5" />

    <ClientOnly v-if="productResponse">
      <TradeInModal />

      <BackUpModal />

      <AddToCartModal
        v-if="selectedOffer && selectedOffer.offerLegacyId"
        :additional-listings
        :listing-id="selectedOffer.offerLegacyId"
        :title="productResponse.titles.raw"
        @close="onAddToCartModalClose"
      />

      <VerifiedRefurbishedModal
        v-if="productResponse.showVerifiedRefurbished"
        :category-id="productResponse.tracking.categoryId"
      />

      <StickyBarBottom
        v-if="enableStickyBarDisplay"
        :price="price!"
        :product="productResponse"
        :selected-offer="selectedOffer!"
        :show-bar="!isAddToCartButtonMobileInViewport"
        :tracking="productTracking"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRouter } from '#imports'
import { computed, onMounted, ref, useTemplateRef } from 'vue'

import BlockSection from '@backmarket/nuxt-layer-cms/BlockSection.vue'
import { logDesync } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import {
  useAddCacheTagHeader,
  useAddServiceCacheTagHeader,
} from '@backmarket/nuxt-module-http-cache/composables/useAddCacheTagHeader'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { useElementVisibility } from '@vueuse/core'

import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { useAddSectionsCacheTagHeader } from '~/scopes/cms/composables/useSetCMSPageCacheTagHeader'
import FavoritesToggle from '~/scopes/dashboard/favorites/toggle/FavoritesToggle.vue'
import { fromProductTrackingModel } from '~/scopes/dashboard/favorites/utils/FavoriteTrackingModel'
import Breadcrumb from '~/scopes/navigation/components/Breadcrumb/Breadcrumb.vue'
import AdditionalInfo from '~/scopes/product/components/AdditionalInfo/AdditionalInfo.vue'
import TradeInModal from '~/scopes/product/components/TradeIn/components/TradeInModal/TradeInModal.vue'
import { useProductHead } from '~/scopes/product/composables/useProductHead/useProductHead'
import { useProductPrice } from '~/scopes/product/composables/useProductPrice'
import { useProductTracking } from '~/scopes/product/composables/useProductTracking'
import AboutBatteryBlock from '~/scopes/product/pages/Product-JP/components/AboutBatteryBlock/AboutBatteryBlock.vue'
import AboutHygieneBlock from '~/scopes/product/pages/Product-JP/components/AboutHygieneBlock/AboutHygieneBlock.vue'
import MobileAtc from '~/scopes/product/pages/Product-JP/components/MobileAtc/MobileAtc.vue'
import StickyBarBottom from '~/scopes/product/pages/Product-JP/components/StickyBarBottom/StickyBarBottom.vue'
import TitleDesktop from '~/scopes/product/pages/Product-JP/components/TitleDesktop/TitleDesktop.vue'
import TitleMobile from '~/scopes/product/pages/Product-JP/components/TitleMobile/TitleMobile.vue'
import TitleMobileAtc from '~/scopes/product/pages/Product-JP/components/TitleMobileAtc/TitleMobileAtc.vue'
import VerifiedRefurbishedModal from '~/scopes/product/pages/Product-JP/components/VerifiedRefurbishedModal/VerifiedRefurbishedModal.vue'
import VerifiedRefurbishedTag from '~/scopes/product/pages/Product-JP/components/VerifiedRefurbishedTag/VerifiedRefurbishedTag.vue'
import { useAddGradeInUrl } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useAddGradeInUrl/useAddGradeInUrl'
import { useGradeFallback } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useGradeFallback/useGradeFallback'
import { useInvalidGradeFallback } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useInvalidGradeFallback/useInvalidGradeFallback'
import { useProductRematch } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useProductRematch/useProductRematch'
import { useRedirectionToQueryParamsURL } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useRedirectionToQueryParamsURL/useRedirectionToQueryParamsURL'
import Accessories from '~/scopes/product-core/components/presentationals/Accessories/Accessories.vue'
import AdvertisedPayment from '~/scopes/product-core/components/presentationals/AdvertisedPayment/AdvertisedPayment.vue'
import CheckpointsCarousel from '~/scopes/product-core/components/presentationals/CheckpointsCarousel/CheckpointsCarousel.vue'
import Gallery from '~/scopes/product-core/components/presentationals/Gallery/Gallery.vue'
import OutOfStock from '~/scopes/product-core/components/presentationals/OutOfStock/OutOfStock.vue'
import SeoContent from '~/scopes/product-core/components/presentationals/SeoContent/SeoContent.vue'
import SeoLinks from '~/scopes/product-core/components/presentationals/SeoLinks/SeoLinks.vue'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import Seller from '~/scopes/product-core/pages/ProductDetailPage/jp/components/JPContainerSeller/components/Seller/Seller.vue'
import { PRODUCT } from '~/scopes/product-core/route-names'
import ReviewsBlock from '~/scopes/reviews/reviews-display/components/ReviewsBlock/ReviewsBlock.vue'

import Recommendations from '../../components/Recommendations/Recommendations.vue'
import { useProductPageSeoLinks } from '../../composables/useProductPageSeoLinks'

import AddToCartModal from './components/AddToCartModal/AddToCartModal.vue'
import BackUpModal from './components/BackUpModal/BackUpModal.vue'
import BlackFridaySpecial from './components/BlackFridaySpecial/BlackFridaySpecial.vue'
import { createCustomizationFunnelData } from './components/CustomizationFunnel/utils/createCustomizationFunnelData'
import CustomizationFunnelWrapper from './components/CustomizationFunnelWrapper/CustomizationFunnelWrapper.vue'
import PaymentMethods from './components/PaymentMethods/PaymentMethods.vue'
import PointsSection from './components/PointsSection/PointsSection.vue'
import ProductConditionBlock from './components/ProductConditionBlock/ProductConditionBlock.vue'
import ReassuranceAboutUs from './components/ReassuranceAboutUs/ReassuranceAboutUs.vue'
import ReassuranceCustomerSupport from './components/ReassuranceCustomerSupport/ReassuranceCustomerSupport.vue'
import ReassuranceInsuranceReturn from './components/ReassuranceInsuranceReturn/ReassuranceInsuranceReturn.vue'
import ReassuranceItems from './components/ReassuranceItems/ReassuranceItems.vue'
import ReassuranceMarketplace from './components/ReassuranceMarketplace/ReassuranceMarketplace.vue'
import ReassuranceWarranty from './components/ReassuranceWarranty/ReassuranceWarranty.vue'
import TabNavigation from './components/TabNavigation/TabNavigation.vue'
import { useProductRequests } from './composables/useProductRequests'

const route = useRoute()
const router = useRouter()

const { grade: urlGrade, offerType, productId } = useUrlParams()
const additionalListings = ref<Array<string>>([])
const outOfStockAlternativeLoading = ref(true)
const outOfStockAlternative = ref<Product | null>(null)
const errorReco = ref(false)
// This is used to check if the add to cart button is in the viewport
// on mobile to hide the sticky bar bottom when it is
const addToCartMobileButtonVisibilityBeacon = useTemplateRef<HTMLDivElement>(
  'addToCartMobileButtonVisibilityBeacon',
)
const addToCartTitleMobileButtonVisibilityBeacon =
  useTemplateRef<HTMLDivElement>('addToCartTitleMobileButtonVisibilityBeacon')
const titleMobileButtonVisibilityBeacon = useTemplateRef<HTMLDivElement>(
  'titleMobileButtonVisibilityBeacon',
)

const isMobileAtcVisible = useElementVisibility(
  addToCartMobileButtonVisibilityBeacon,
)
const isTitleMobileAtcVisible = useElementVisibility(
  addToCartTitleMobileButtonVisibilityBeacon,
)

const isTitleVisible = useElementVisibility(titleMobileButtonVisibilityBeacon)

// Combine both visibility states
const isAddToCartButtonMobileInViewport = computed(
  () =>
    isMobileAtcVisible.value ||
    isTitleMobileAtcVisible.value ||
    isTitleVisible.value,
)

const breakpoint = useClientOnlyComputedBreakpoint()
const isMobile = computed(() => breakpoint.value < Breakpoint.MD)

const {
  productResponse,
  breadcrumbResponse,
  technicalSpecificationsResponse,
  pickersResponse,
  pickersResponseCacheTagHeader,
  isPickersResponsePending,
  ratesResponse,
  reviewsResponse,
  isReviewsResponsePending,
  isOutOfStock,
  selectedOffer,
  popularityStatsSalesResponse,
  cmsSections,
  reviewsSummaryPending,
  reviewsSummaryResponse,
  reviewsAiSummaryPending,
  reviewsAISummary,
} = await useProductRequests(productId, urlGrade, offerType)

// Set cache Header for Cloudflare
useAddCacheTagHeader(pickersResponseCacheTagHeader)

useAddServiceCacheTagHeader()

useAddSectionsCacheTagHeader(cmsSections.value?.id)

const reviews = computed(() => ({
  isPending: isReviewsResponsePending.value,
  list: reviewsResponse.value ? reviewsResponse.value.results : [],
}))

useProductRematch(productResponse)
useInvalidGradeFallback()
useGradeFallback(pickersResponse)
useAddGradeInUrl(selectedOffer)
useRedirectionToQueryParamsURL()
useProductHead(
  productResponse,
  selectedOffer,
  ratesResponse,
  reviewsResponse,
  technicalSpecificationsResponse,
  isOutOfStock,
)
const productTracking = useProductTracking(
  productResponse,
  selectedOffer,
  ratesResponse,
  pickersResponse,
  isPickersResponsePending,
  ref(undefined),
  ref(null),
)
const { price, priceWithoutSubsidies, priceWithoutTradeIn } = useProductPrice({
  pickers: ref(undefined),
  selectedOffer,
})

function onAddToCartModalClose() {
  additionalListings.value = []
}

function onBundleAddToCart(listings: Array<string>) {
  additionalListings.value = listings
}

function onRecommendationsLoaded(products: Array<Product>) {
  outOfStockAlternative.value = products[0]
  outOfStockAlternativeLoading.value = false
}

function onRecommendationsError() {
  errorReco.value = true
  outOfStockAlternativeLoading.value = false
}

function handleShowTopBar() {
  const header = document.getElementById('header')

  if (header && header !== null) {
    header.style.visibility = 'hidden'
  }
}
function handleHideTopBar() {
  const header = document.getElementById('header')

  if (header && header !== null) {
    header.style.visibility = 'visible'
  }
}
const i18n = useI18n()

const experiments = useExperiments()
const isBlackFridaySpecialActive = computed(() => {
  const isFeatureFlagEnabled =
    experiments['experiment.ppJapanBlackFridaySpecial'] ===
    'withBlackFridaySpecial'
  const isPriceEligible = price.value && Number(price.value.amount) >= 20000

  return isFeatureFlagEnabled && isPriceEligible
})

const isHygieneBlockActive = computed(() => {
  return experiments['experiment.ppJapanHygieneBlock'] === 'withHygieneBlock'
})

// Create the funnel data - make sure this is properly defined
const funnelData = computed(() => {
  // Check if we have the required data
  if (!pickersResponse.value?.pickerGroups) {
    return []
  }

  // For Product-JP, use the simplified version
  return createCustomizationFunnelData(
    i18n,
    pickersResponse.value.pickerGroups,
    experiments,
  )
})

const productPageUrl = computed(() => {
  if (!productResponse.value) {
    return ''
  }

  return router.resolve(
    toNuxtLink({
      name: PRODUCT.HOME,
      params: {
        locale: toBcp47Locale(i18n.currentLocale),
        slugV2: productResponse.value?.seo.slug,
        uuid: productResponse.value?.productId,
      },
    }),
  ).href
})

const seoLinksList = useProductPageSeoLinks(productPageUrl.value)

onMounted(() => {
  if (productResponse.value?.productId) {
    logDesync({
      id: productResponse.value.productId,
      isOutOfStock: isOutOfStock.value,
      price: selectedOffer.value?.price?.amount,
    })
  }
})

const enableStickyBarDisplay = computed(() => {
  return Boolean(
    !isOutOfStock.value &&
      productResponse.value &&
      selectedOffer.value &&
      price.value,
  )
})
const favoritesTrackingInfo = computed(() => {
  return fromProductTrackingModel(productTracking.product.value)
})
</script>

<style>
/* On mobile, we need to add some padding to be able to see everything in the footer due to the sticky add to cart bar */
@media only screen and (max-width: 767px) {
  #main-footer {
    padding-bottom: 100px !important;
  }
}
</style>
